import { createPortal } from 'react-dom'

import type { RobotMediaItem } from '../../../services/api/domains/robots.api'
import { MediaLightbox } from '../../molecules/MediaLightbox/MediaLightbox'
import { formatBytes, formatDuration } from './robot-media-display'
import { RobotMediaView } from './RobotMediaView'
import { useResolvedMediaUrl } from './useResolvedMediaUrl'

export interface RobotMediaLightboxProps {
  /** Only openable items — the index is taken against THIS list, never the raw one. */
  items: RobotMediaItem[]
  index: number
  onClose: () => void
  onIndexChange: (next: number) => void
}

function caption(item: RobotMediaItem): string {
  const parts = [
    item.deviceFile ?? new Date(item.capturedAt).toLocaleString(),
    formatDuration(item.durationMs),
    formatBytes(item.sizeBytes),
  ].filter(Boolean)

  return parts.join(' · ')
}

/**
 * Full-screen viewer for one stored capture.
 *
 * Video playback is a plain `<video src>` on a presigned MP4 and should stay that way: a static
 * file over HTTP needs no Media Source Extensions, no segment stitching and no shared clock. The
 * live-stream players in this app exist because a live camera has none of those properties.
 */
export function RobotMediaLightbox({ items, index, onClose, onIndexChange }: RobotMediaLightboxProps) {
  const current = items[index]
  const { url, handleError, handleLoad } = useResolvedMediaUrl(current?.key, current?.robotId)

  if (!current) return null

  // Portalled to the body. `position: fixed` is contained by any ancestor with a transform or
  // filter, and the dashboard's pane chrome has both — so without this "fullscreen" would be
  // confined to the tile it was opened from.
  return createPortal(
    <MediaLightbox
      count={items.length}
      index={index}
      onClose={onClose}
      onIndexChange={onIndexChange}
      caption={caption(current)}
    >
      <RobotMediaView
        item={current}
        url={url}
        onReady={handleLoad}
        onError={handleError}
        className="max-h-[80vh] max-w-[80vw] object-contain"
      />
    </MediaLightbox>,
    document.body,
  )
}
